<template>
    <Toast/>
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <!-- Cabecera con información de la cuenta -->
                <div class="account-header">
                    <div class="account-info">
                        <h3 class="account-name">{{ account.name }}</h3>
                        <div class="account-details">
                            <span v-if="account.moneyType === 'TRANSFER' && account.bank" class="account-bank">
                                <img v-if="account.bank.icon" 
                                     :src="$baseURL + account.bank.icon" 
                                     :alt="account.bank.name"
                                     class="bank-icon-small"
                                />
                                {{ account.bank.name }}
                            </span>
                            <span v-if="account.accountNumber" class="account-number">
                                {{ getAccountNumberDisplay() }}
                            </span>
                        </div>
                        <div class="account-balance">
                            <span class="balance-label">Saldo disponible</span>
                            <span class="balance-amount">$ {{ formatBalance(account.balance) }}</span>
                        </div>
                    </div>
                    <div class="account-actions">
                        <Button label="Agregar Dinero" icon="pi pi-plus-circle" class="p-button-success mb-2" @click="openAddMoney" />
                        <Button label="Retirar Dinero" icon="pi pi-minus-circle" class="p-button-warning mb-2" @click="openWithdrawMoney" />
                        <!-- <Button label="Transferir" icon="pi pi-arrow-right-arrow-left" class="p-button-info mb-2" @click="openTransfer" /> -->
                    </div>
                </div>

                <Divider />

                <!-- Tabla de transacciones -->
                <DataTable 
                    ref="dt" 
                    :value="displayedTransactions" 
                    dataKey="id" 
                    :lazy="true"
                    :paginator="true" 
                    :rows="pageSize"
                    :totalRecords="totalRecords"
                    :first="(currentPage - 1) * pageSize"
                    :filters="filters"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" 
                    :rowsPerPageOptions="[50]"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} transacciones" 
                    responsiveLayout="scroll"
                    :loading="loading"
                    @page="onPageChange"
                >
                    <template #header>
                        <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
                            <h5 class="m-0">Historial de Transacciones</h5>
                            <div class="flex flex-column md:flex-row gap-2 mt-2 md:mt-0">
                                <span class="p-input-icon-left">
                                    <i class="pi pi-search" />
                                    <InputText v-model="filters['global'].value" placeholder="Buscar..." />
                                </span>
                                <Calendar 
                                    v-model="initialDate" 
                                    placeholder="Fecha inicial" 
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    :showButtonBar="true"
                                />
                                <Calendar 
                                    v-model="endDate" 
                                    placeholder="Fecha final" 
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    :showButtonBar="true"
                                />
                                <Button 
                                    icon="pi pi-search" 
                                    class="p-button-rounded" 
                                    @click="applyDateFilter"
                                    v-tooltip.top="'Aplicar filtro de fechas'"
                                />
                            </div>
                        </div>
                    </template>

                    <template #empty>
                        <div class="text-center p-4">
                            <i class="pi pi-inbox" style="font-size: 3rem; color: var(--text-color-secondary);"></i>
                            <p class="mt-3" style="color: var(--text-color-secondary);">No hay transacciones registradas</p>
                        </div>
                    </template>

                    <Column field="createdAt" header="Fecha" :sortable="true" headerStyle="width:25%; min-width:12rem;">
                        <template #body="slotProps">
                            <span class="p-column-title">Fecha</span>
                            {{ formatDate(slotProps.data.createdAt) }}
                        </template>
                    </Column>

                    <Column field="reference" header="Referencia" :sortable="true" headerStyle="width:35%; min-width:12rem;">
                        <template #body="slotProps">
                            <span class="p-column-title">Referencia</span>
                            <router-link 
                                v-if="slotProps.data.saleId" 
                                :to="`/dashboard/venta/form?id=${slotProps.data.saleId}`"
                                class="reference-link"
                            >
                                {{ slotProps.data.reference || 'N/A' }}
                            </router-link>
                            <router-link 
                                v-else-if="slotProps.data.purchaseId" 
                                :to="`/dashboard/compra/form?id=${slotProps.data.purchaseId}`"
                                class="reference-link"
                            >
                                {{ slotProps.data.reference || 'N/A' }}
                            </router-link>
                            <span v-else>{{ slotProps.data.reference || 'N/A' }}</span>
                        </template>
                    </Column>
                    <Column field="amount" header="Monto" :sortable="true" headerStyle="width:15%; min-width:10rem;">
                        <template #body="slotProps">
                            <span class="p-column-title">Monto</span>
                            <span :class="getAmountClass(slotProps.data.totalMoved)">
                                {{ formatAmount(slotProps.data.totalMoved) }}
                            </span>
                        </template>
                    </Column>

                    <Column field="accountBalance" header="Saldo" :sortable="true" headerStyle="width:15%; min-width:10rem;">
                        <template #body="slotProps">
                            <span class="p-column-title">Saldo</span>
                            <span class="balance-value">
                                $ {{ formatBalance(slotProps.data.accountBalance) }}
                            </span>
                        </template>
                    </Column>

                    <Column field="creator" header="Creador" :sortable="true" headerStyle="width:15%; min-width:10rem;">
                        <template #body="slotProps">
                            <span class="p-column-title">Creador</span>
                            {{ slotProps.data.creator || 'Sistema' }}
                        </template>
                    </Column>

                    <Column header="Comprobante" headerStyle="width:10%; min-width:8rem;" bodyClass="text-center">
                        <template #body="slotProps">
                            <span class="p-column-title">Comprobante</span>
                            <Button 
                                v-if="slotProps.data.proof" 
                                icon="pi pi-image" 
                                class="p-button-rounded p-button-text p-button-info" 
                                @click="viewProof(slotProps.data.proof)"
                                v-tooltip.top="'Ver comprobante'"
                            />
                            <span v-else class="text-muted">-</span>
                        </template>
                    </Column>
                </DataTable>

                <!-- Dialog para ver comprobante -->
                <Dialog v-model:visible="proofDialog" :style="{width: '600px', maxWidth: '90vw'}" header="Comprobante de Transacción" :modal="true">
                    <div class="proof-container">
                        <img :src="currentProof" alt="Comprobante" class="proof-image" />
                    </div>
                    <template #footer>
                        <Button label="Cerrar" icon="pi pi-times" class="p-button-secondary" @click="proofDialog = false"/>
                        <a :href="currentProof" target="_blank" download>
                            <Button label="Descargar" icon="pi pi-download" class="p-button-primary"/>
                        </a>
                    </template>
                </Dialog>

                <!-- Dialog para agregar/retirar dinero -->
                <Dialog v-model:visible="transactionDialog" :style="{width: '500px', maxWidth: '90vw'}" :header="transactionDialogHeader" :modal="true" class="p-fluid">
                    <div class="field">
                        <label for="amount">Monto *</label>
                        <InputNumber 
                            id="amount" 
                            v-model="transaction.amount" 
                            mode="currency" 
                            currency="USD" 
                            locale="en-US"
                            :min="0.01"
                            :class="{'p-invalid': submitted && !transaction.amount}"
                        />
                        <small class="p-invalid" v-if="submitted && !transaction.amount">El monto es requerido.</small>
                    </div>

                    <div class="field">
                        <label for="reference">Referencia</label>
                        <InputText 
                            id="reference" 
                            v-model.trim="transaction.reference" 
                            placeholder="Descripción de la transacción"
                        />
                    </div>

                    <div v-if="account.moneyType === 'TRANSFER'" class="field">
                        <label for="evidence">Comprobante (opcional)</label>
                        <FileUpload 
                            mode="basic" 
                            name="evidence"
                            :maxFileSize="5000000"
                            accept="image/*,application/pdf"
                            :auto="false"
                            chooseLabel="Seleccionar archivo"
                            @select="onFileSelect"
                            @clear="onFileClear"
                        />
                        <small class="text-muted">Formatos aceptados: Imágenes (JPG, PNG) o PDF. Máximo 5MB.</small>
                        <div v-if="transaction.evidenceFile" class="mt-2">
                            <Chip :label="transaction.evidenceFile.name" icon="pi pi-file" removable @remove="onFileClear" />
                        </div>
                    </div>

                    <template #footer>
                        <Button label="Cancelar" icon="pi pi-times" class="p-button p-button-secondary" @click="hideTransactionDialog"/>
                        <Button :label="transactionType === 'add' ? 'Agregar' : 'Retirar'" icon="pi pi-check" :class="transactionType === 'add' ? 'p-button-success' : 'p-button-warning'" @click="saveTransaction" />
                    </template>
                </Dialog>
            </div>
        </div>
    </div>
</template>

<script>
import Big from 'big.js';
import { FilterMatchMode } from 'primevue/api';
import FinantialAccount from '../../models/finantialAccount';
import FinantialAccountTransaction from '../../models/finantialAccountTransaction';
import httpRequest from '../../service/httpRequest';
import { compressImage, IMAGE_MAX_BYTES } from '../../service/imageCompression';

export default {
    data() {
        return {
            account: new FinantialAccount(),
            allTransactions: [],  // Array completo de todas las transacciones cargadas
            loadedPages: new Set(),  // Set de páginas que ya hemos cargado del servidor
            filters: {},
            loading: false,
            transactionDialog: false,
            transactionType: 'add', // 'add' or 'withdraw'
            transaction: {
                amount: null,
                reference: '',
                evidenceFile: null
            },
            submitted: false,
            proofDialog: false,
            currentProof: null,
            // Paginación del servidor
            currentPage: 1,
            pageSize: 50,
            totalRecords: 0,
            totalPages: 0,
            // Filtros de fecha
            initialDate: null,
            endDate: null,
        };
    },
    computed: {
        transactionDialogHeader() {
            return this.transactionType === 'add' ? 'Agregar Dinero' : 'Retirar Dinero';
        },
        displayedTransactions() {
            // Mostrar solo las transacciones de la página actual
            const startIndex = (this.currentPage - 1) * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            return this.allTransactions.slice(startIndex, endIndex);
        }
    },
    created() {
        this.initFilters();
        this.loadAccountData();
    },
    methods: {
        initFilters() {
            this.filters = {
                'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
            };
        },
        async loadAccountData() {
            const accountId = this.$route.query.id;
            
            if (!accountId) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se especificó el ID de la cuenta',
                    life: 4000
                });
                this.$router.push('/dashboard/cuenta');
                return;
            }

            this.loading = true;
            try {
                const response = await httpRequest.getFinantialAccount(
                    accountId, 
                    this.currentPage, 
                    this.pageSize,
                    this.initialDate,
                    this.endDate
                );
                
                if (response.status === 200 && response.data) {
                    // La respuesta ahora contiene {account: {}, transactions: [], pagination: {}}
                    if (response.data.account) {
                        this.account = new FinantialAccount(response.data.account);
                    }
                    
                    if (response.data.transactions) {
                        const newTransactions = response.data.transactions.map(item => 
                            new FinantialAccountTransaction(item)
                        );
                        
                        // Calcular el índice de inicio para insertar las transacciones
                        const startIndex = (this.currentPage - 1) * this.pageSize;
                        
                        // Insertar las transacciones en la posición correcta
                        newTransactions.forEach((transaction, index) => {
                            this.allTransactions[startIndex + index] = transaction;
                        });
                        
                        // Marcar esta página como cargada
                        this.loadedPages.add(this.currentPage);
                    }

                    // Actualizar información de paginación
                    if (response.data.pagination) {
                        this.totalRecords = response.data.pagination.total;
                        this.currentPage = response.data.pagination.page;
                        this.pageSize = response.data.pagination.pageSize;
                        this.totalPages = response.data.pagination.totalPages;
                    }
                } else {
                    console.error('Error al cargar cuenta:', response);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo cargar la cuenta financiera',
                        life: 4000
                    });
                    this.$router.push('/dashboard/cuenta');
                }
            } catch (error) {
                console.error('Error al cargar cuenta:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Ocurrió un error al cargar la cuenta',
                    life: 4000
                });
                this.$router.push('/dashboard/cuenta');
            } finally {
                this.loading = false;
            }
        },
        async onPageChange(event) {
            // event.page es 0-indexed, pero el backend espera 1-indexed
            const newPage = event.page + 1;
            const newPageSize = event.rows;
            
            // Si cambió el tamaño de página, limpiar caché y recargar
            if (newPageSize !== this.pageSize) {
                this.allTransactions = [];
                this.loadedPages.clear();
                this.pageSize = newPageSize;
            }
            
            this.currentPage = newPage;
            
            // Solo cargar del servidor si no tenemos esta página en caché
            if (!this.loadedPages.has(this.currentPage)) {
                await this.loadAccountData();
            }
        },
        applyDateFilter() {
            // Limpiar caché y volver a la primera página cuando se aplica un filtro de fecha
            this.allTransactions = [];
            this.loadedPages.clear();
            this.currentPage = 1;
            this.loadAccountData();
        },
        openAddMoney() {
            this.transactionType = 'add';
            this.transaction = {
                amount: null,
                reference: '',
                evidenceFile: null
            };
            this.submitted = false;
            this.transactionDialog = true;
        },
        openWithdrawMoney() {
            this.transactionType = 'withdraw';
            this.transaction = {
                amount: null,
                reference: '',
                evidenceFile: null
            };
            this.submitted = false;
            this.transactionDialog = true;
        },
        openTransfer() {
            // TODO: Implementar funcionalidad de transferir entre cuentas
            this.$toast.add({
                severity: 'info',
                summary: 'Próximamente',
                detail: 'Funcionalidad de transferir en desarrollo',
                life: 3000
            });
        },
        hideTransactionDialog() {
            this.transactionDialog = false;
            this.submitted = false;
        },
        viewProof(proofUrl) {
            this.currentProof = this.$baseURL + proofUrl;
            this.proofDialog = true;
        },
        async onFileSelect(event) {
            // El adjunto puede ser imagen o PDF. Solo comprimimos imágenes; el
            // PDF pasa intacto (lo gobierna el maxFileSize del FileUpload).
            const original = event.files && event.files[0];
            if (!original) { this.transaction.evidenceFile = null; return; }
            const isImage = original.type && original.type.indexOf('image/') === 0;
            let file = original;
            if (isImage) {
                try {
                    file = await compressImage(original);
                } catch (e) {
                    file = original;
                }
                if (file.size > IMAGE_MAX_BYTES) {
                    this.transaction.evidenceFile = null;
                    this.$toast.add({severity:'error', summary:'Imagen muy pesada', detail:'No se pudo reducir la imagen por debajo de 1MB. Intente con una captura o una foto de menor resolución.', life:6000});
                    return;
                }
            }
            this.transaction.evidenceFile = file;
        },
        onFileClear() {
            this.transaction.evidenceFile = null;
        },
        async saveTransaction() {
            this.submitted = true;

            // Validar que el monto sea válido
            if (!this.transaction.amount || this.transaction.amount <= 0) {
                return;
            }

            try {
                // El monto es negativo si es un retiro
                const finalAmount = this.transactionType === 'add' 
                    ? this.transaction.amount 
                    : -Math.abs(this.transaction.amount);
                
                // Obtener el metodo de pago
                const paymentMethod = this.account.moneyType === 'CASH' ? 'CASH' : 'TRANSFER';

                // Llamar al backend para crear la transacción (movimiento libre,
                // sin venta ni compra asociada). Usamos el endpoint batch con
                // un único item para mantener un solo camino de creación.
                const response = await httpRequest.createMultipleTransactions([{
                    accountId: this.account.id,
                    amount: finalAmount,
                    reference: this.transaction.reference || '',
                    paymentMethod,
                    proof: this.transaction.evidenceFile || null,
                }]);
                
                if (response.status === 200 && response.data) {
                    // Actualizar el balance de la cuenta con la nueva transacción
                    const finalAmountBig = new Big(finalAmount);
                    this.account.balance = this.account.balance.plus(finalAmountBig);
                    
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Exitoso',
                        detail: `Transacción ${this.transactionType === 'add' ? 'agregada' : 'registrada'} correctamente`,
                        life: 3000
                    });

                    this.transactionDialog = false;
                    this.transaction = {
                        amount: null,
                        reference: '',
                        evidenceFile: null
                    };

                    // Limpiar caché, volver a la primera página y recargar datos
                    this.allTransactions = [];
                    this.loadedPages.clear();
                    this.currentPage = 1;
                    await this.loadAccountData();
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo crear la transacción',
                        life: 4000
                    });
                }
            } catch (error) {
                console.error('Error al crear transacción:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Ocurrió un error al crear la transacción',
                    life: 4000
                });
            }
        },
        getAccountNumberDisplay() {
            if (this.account.accountNumber) {
                const s = String(this.account.accountNumber);
                return '******' + s.slice(-4);
            }
            return '';
        },
        formatBalance(balance) {
            try {
                if (balance == null) return '0.00';
                const b = balance instanceof Big ? balance : new Big(balance);
                return b.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            } catch (e) {
                return '0.00';
            }
        },
        formatAmount(amount) {
            try {
                if (amount == null) return '$0.00';
                const a = amount instanceof Big ? amount : new Big(amount);
                const sign = a.lt(0) ? '-' : '+';
                const absValue = a.abs().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                return sign + ' $' + absValue;
            } catch (e) {
                return '$0.00';
            }
        },
        getAmountClass(amount) {
            try {
                const a = amount instanceof Big ? amount : new Big(amount);
                return a.lt(0) ? 'amount-negative' : 'amount-positive';
            } catch (e) {
                return '';
            }
        },
        formatDate(date) {
            if (!date) return 'N/A';
            const d = date instanceof Date ? date : new Date(date);
            return d.toLocaleDateString('es-EC', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
};
</script>

<style scoped>
.account-header {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 1rem;
}

@media screen and (min-width: 768px) {
    .account-header {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
    }
}

.account-info {
    flex: 1;
}

.account-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-color);
    margin: 0 0 0.5rem 0;
}

.account-details {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 1rem;
}

.account-bank {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-color-secondary);
    font-size: 0.95rem;
}

.bank-icon-small {
    height: 20px;
    width: auto;
    object-fit: contain;
}

.account-number {
    color: var(--text-color-secondary);
    font-size: 0.9rem;
    font-family: monospace;
}

.account-balance {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.balance-label {
    color: var(--text-color-secondary);
    font-size: 0.875rem;
}

.balance-amount {
    font-size: 2rem;
    font-weight: 700;
    color: var(--primary-color);
}

.tip-breakdown {
    display: block;
    color: var(--text-color-secondary);
    font-size: 0.75rem;
    margin-top: 0.15rem;
}

.account-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 200px;
}

.amount-positive {
    color: #22c55e;
    font-weight: 600;
}

.amount-negative {
    color: #ef4444;
    font-weight: 600;
}

.balance-value {
    font-weight: 600;
    color: var(--text-color);
}

.reference-link {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
}

.reference-link:hover {
    color: #1e40af; /* Azul más oscuro visible sobre cualquier fondo */
    text-decoration: underline;
}

.proof-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 300px;
    background-color: var(--surface-50);
    border-radius: 6px;
    overflow: hidden;
}

.proof-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
}

.text-muted {
    color: var(--text-color-secondary);
}
</style>
